import React, { useState, useMemo, useEffect, useRef } from "react";
import { tw } from "@careinfo/tailwind";
import useGetHook from "../../../customHooks/useGetHook";
import APIS from "../../../constant/Endpoint";
import debounce from "lodash.debounce";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const MultiEmailInput = ({ label, value, onChange, error }) => {
  const [input, setInput] = useState("");
  const [inlineError, setInlineError] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [search, setSearch] = useState("");
  const [contactEmails, setContactEmails] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debouncedSearch = useMemo(() => debounce(setSearch, 300), []);

  useEffect(() => {
    if (input.trim()) {
      debouncedSearch(input.trim());
    }
  }, [input]);

  const { data, isLoading } = useGetHook({
    queryKey: ["contacts", `conatcts-${search}`],
    url: APIS.SEARCH,
    params: {
      search: search,
    },
  });

  useEffect(() => {
    if (Array.isArray(data)) {
      setContactEmails(data ?? []);
    }
  }, [data]);

  const filteredSuggestions = useMemo(() => {
    const suggestions = [];

    for (const contact of contactEmails) {
      if (Array.isArray(contact.emails)) {
        const matchingEmails = contact.emails.filter(
          (e) => e.email
          // e.email.toLowerCase().includes(input.toLowerCase()) &&
          // !value.includes(e.email)
        );

        if (matchingEmails.length > 0) {
          suggestions.push({ ...contact, matchedEmails: matchingEmails });
        }
      } else if (
        contact.email &&
        contact.email.toLowerCase().includes(input.toLowerCase()) &&
        !value.includes(contact.email)
      ) {
        suggestions.push(contact);
      }

      if (suggestions.length >= 5) break;
    }

    return suggestions;
  }, [contactEmails, input, value]);

  const addEmail = (contact) => {
    if (typeof contact === "string") {
      if (!isValidEmail(contact)) {
        setInlineError("Invalid email address");
        return;
      }
      if (!value.includes(contact)) {
        onChange([...value, contact]);
      }
    } else if (contact?.email) {
      if (isValidEmail(contact.email) && !value.includes(contact.email)) {
        onChange([...value, contact.email]);
      }
    } else if (Array.isArray(contact?.emails)) {
      const newEmails = contact.emails
        .map((e) => e.email)
        .filter((email) => isValidEmail(email) && !value.includes(email));
      if (newEmails.length > 0) {
        onChange([...value, ...newEmails]);
      }
    }

    setInput("");
    setInlineError("");
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    const suggestionsLength = filteredSuggestions.length;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestionsLength - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestionsLength - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestionsLength) {
        addEmail(filteredSuggestions[highlightedIndex]);
      } else {
        const trimmed = input.trim();
        if (trimmed) addEmail(trimmed);
      }
    } else if (e.key === ",") {
      e.preventDefault();
      const trimmed = input.trim();
      if (trimmed) addEmail(trimmed);
    }
  };

  const handleRemove = (email) => {
    onChange(value.filter((e) => e !== email));
  };
  const containerRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredSuggestions, input]);

  return (
    <div ref={containerRef} className={tw`relative`}>
      {label && (
        <label className={tw`block mb-1 text-sm font-medium`}>{label}</label>
      )}
      <div
        className={tw`flex flex-wrap bg-[#EEF5F4] items-center gap-2 border rounded px-2 py-1 min-h-[40px]`}
      >
        {value.map((email, idx) => (
          <span
            key={idx}
            className={tw`bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full flex items-center`}
          >
            {email}
            <button
              type="button"
              className={tw`ml-1 text-red-600 hover:text-red-800 focus:outline-none`}
              onClick={() => handleRemove(email)}
            >
              &times;
            </button>
          </span>
        ))}
        <input
          className={tw`flex-1 border-none bg-[#EEF5F4] outline-none min-w-[100px]`}
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setShowSuggestions(true);
            if (inlineError) setInlineError("");
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder="Type email and press Enter or ,"
        />
      </div>
      {showSuggestions && filteredSuggestions.length > 0 && (
        <ul
          className={tw`absolute z-10 bg-white border rounded shadow w-full mt-1 max-h-48 overflow-auto`}
        >
          {filteredSuggestions.map((contact, index) => {
            const isHighlighted = index === highlightedIndex;
            const isGroup = Array.isArray(contact.emails);
            const display = isGroup
              ? `${contact.name}`
              : `${contact.email}, ${contact.name}`;

            return (
              <li
                key={index}
                className={tw`px-3 py-2 cursor-pointer text-sm ${
                  isHighlighted ? "bg-gray-200" : "hover:bg-gray-100"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addEmail(contact);
                }}
              >
                {display}
              </li>
            );
          })}
        </ul>
      )}

      <div className={tw`min-h-[10px] mt-1`}>
        <p
          className={tw`text-xs text-gray-500 transition-opacity duration-200`}
          style={{
            visibility: input ? "visible" : "hidden",
            opacity: input ? 1 : 0,
          }}
        >
          Press <span className={tw`font-medium`}>Enter</span> or{" "}
          <span className={tw`font-medium`}>,</span> to add an email
        </p>
      </div>

      {(inlineError || error) && (
        <p className={tw`text-red-500 text-sm mt-1`}>
          {inlineError || error.message}
        </p>
      )}
    </div>
  );
};

export default MultiEmailInput;
